import { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const NewsletterForm = ({
  source = 'footer',
  variant = 'light',
  compact = false, // kept for backward compat; layout is now the same expanded form
  testId = 'newsletter-form',
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobtitle, setJobtitle] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      setStatus('error');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          jobtitle: jobtitle.trim() || null,
          company: company.trim() || null,
          source,
        }),
      });
      if (!res.ok) throw new Error('subscribe_failed');
      setStatus('success');
      window.gtag?.('event', 'newsletter_signup', { source });
      window.lintrk?.('track', { conversion_id: 'newsletter_signup' });
    } catch (err) {
      setStatus('error');
      setError('Something went wrong. Please try again.');
    }
  };

  const dark = variant === 'dark';
  const textColor = dark ? 'text-[#FDFBF7]' : 'text-[#0F0F0F]';
  const mutedColor = dark ? 'text-[#9A9A9A]' : 'text-[#5A5A5A]';
  const inputBg = dark
    ? 'bg-[#1A1A1A] border-[#C5A880]/30 text-[#FDFBF7] placeholder:text-[#5A5A5A]'
    : 'bg-white border-[#E5E0D8] text-[#0F0F0F] placeholder:text-[#9A9A9A]';

  if (status === 'success') {
    return (
      <div data-testid={`${testId}-success`} className={`flex items-center gap-3 ${textColor}`}>
        <Check size={18} className="text-[#C5A880]" />
        <span className={`text-sm ${mutedColor}`}>You're in. Watch your inbox.</span>
      </div>
    );
  }

  const inputCls = `w-full border ${inputBg} px-4 py-3 text-sm focus:outline-none focus:border-[#C5A880] transition-colors`;

  return (
    <form data-testid={testId} onSubmit={handleSubmit} className="w-full">
      {!compact && (
        <p className={`text-sm ${mutedColor} mb-4 leading-relaxed`}>
          Get operator news, cohort dates &amp; career insights — join the list.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid={`${testId}-first-name`}
          placeholder="First name"
          className={inputCls}
        />
        <input
          type="text"
          required
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          data-testid={`${testId}-last-name`}
          placeholder="Last name"
          className={inputCls}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          value={jobtitle}
          onChange={(e) => setJobtitle(e.target.value)}
          data-testid={`${testId}-jobtitle`}
          placeholder="Position (e.g. Cabin Crew)"
          className={inputCls}
        />
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          data-testid={`${testId}-company`}
          placeholder="Company (optional)"
          className={inputCls}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={`${testId}-email`}
          placeholder="your@email.com"
          className={`flex-1 border ${inputBg} px-4 py-3 text-sm focus:outline-none focus:border-[#C5A880] transition-colors`}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          data-testid={`${testId}-submit`}
          className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all disabled:opacity-60"
        >
          {status === 'loading' ? 'Subscribing…' : 'Join'} <ArrowRight size={14} />
        </button>
      </div>
      {status === 'error' && error && (
        <p data-testid={`${testId}-error`} className="mt-2 text-xs text-red-500">{error}</p>
      )}
    </form>
  );
};

export default NewsletterForm;
